import { DesignChannel } from 'components/NewFileModal/Types'
import { StartWithMedia } from '../../components/StartWithMedia/StartWithMedia'
import { PropsWithChildren, useMemo, useState } from 'react'
import { IMediaFile } from 'components/Design/UploadsTab/Types'
import { SetupBasics } from '../../components/SetupBasics/SetupBasics'
import { RefineDetails } from '../../components/RefineDetails/RefineDetails'
import { ChooseTemplate } from '../../components/ChooseTemplate/ChooseTemplate'
import { observer } from 'mobx-react-lite'
import { useMobileWizardCreateDesign } from 'components/Wizards/MobileWizards/hooks/useMobileWizardCreateDesign'
import { MultiStepMobileWizard } from 'components/Wizards/MobileWizards/MultiStepMobileWizard/MultiStepMobileWizard'
import { useFlipperFlag } from 'hooks/useFlipperFlag'
import { CustomiseLayoutSchema, StartWithImageSchema, StartWithVideoSchema } from './schema'
import { CustomiseLayout } from '../../components/CustomiseLayout/CustomiseLayout'
import { SubmitStepProps } from 'components/Wizards/MobileWizards/MultiStepMobileWizard/Types'
import { DesignValues, FlowProps } from 'components/Wizards/MobileWizards/Types'
import { useVisualElement } from 'hooks/queries/visualElements/useVisualElement'
import { VisualElementsClient } from 'api/clients/VisualElementClient'
import { SUCCESS_RESPONSE_TYPE } from 'api/constants'
import { reportError } from 'utils/error'

type Values = Pick<
  DesignValues,
  | 'initial_media_file_id'
  | 'initial_media_file_remove_background'
  | 'width'
  | 'height'
  | 'filename'
  | 'project'
  | 'source_visual_element_id'
  | 'topic'
  | 'brand_voice_id'
  | 'tone'
  | 'language'
  | 'brand_kit_id'
  | 'content'
  | 'palette'
  | 'text_styles'
>

const FlowA2WithInitialTemplateWithVideo = observer(
  ({ onClose, initialValues, schema, ...props }: FlowProps<DesignChannel>) => {
    const schemaOverride = useMemo(
      () => ({
        ...schema,
        steps: [StartWithVideoSchema, ...schema.steps.slice(1, 3)],
      }),
      [schema],
    )

    const [selectedMedia, setSelectedMedia] = useState<IMediaFile | undefined>()

    return (
      <FlowA2 initialValues={initialValues} schema={schemaOverride} onClose={onClose} {...props}>
        <StartWithMedia onSelect={setSelectedMedia} hideAltWizard />
        {selectedMedia && <SetupBasics selectedMedia={selectedMedia} hideAspectRatio />}
        <RefineDetails submitLabel="Generate Post" />
      </FlowA2>
    )
  },
)

const FlowA2WithInitialTemplateWithReplaceableImage = observer(
  ({ onClose, initialValues, schema, ...props }: FlowProps<DesignChannel>) => {
    const schemaOverride = useMemo(
      () => ({
        ...schema,
        steps: [StartWithImageSchema, ...schema.steps.slice(1, 3)],
      }),
      [schema],
    )

    const [selectedMedia, setSelectedMedia] = useState<IMediaFile | undefined>()

    return (
      <FlowA2 initialValues={initialValues} schema={schemaOverride} onClose={onClose} {...props}>
        <StartWithMedia onSelect={setSelectedMedia} hideAltWizard />
        {selectedMedia && <SetupBasics selectedMedia={selectedMedia} hideAspectRatio />}
        <RefineDetails submitLabel="Generate Post" />
      </FlowA2>
    )
  },
)

const FlowA2WithInitialTemplateWithoutReplaceableImage = observer(
  ({ onClose, initialValues, schema, ...props }: FlowProps<DesignChannel>) => {
    const schemaOverride = useMemo(
      () => ({
        ...schema,
        steps: [schema.steps[2]],
      }),
      [schema],
    )

    return (
      <FlowA2 initialValues={initialValues} schema={schemaOverride} onClose={onClose} {...props}>
        <RefineDetails submitLabel="Generate Post" />
      </FlowA2>
    )
  },
)

const FlowA2WithoutInitialTemplate = observer(({ onClose, initialValues, ...props }: FlowProps<DesignChannel>) => {
  const layoutCustomisationEnabled = useFlipperFlag('mobile_redesign_wizard_A1_layout_customisation')
  const schema = useMemo(() => {
    if (layoutCustomisationEnabled) {
      return {
        ...props.schema,
        steps: [...props.schema.steps, CustomiseLayoutSchema],
      }
    }

    return props.schema
  }, [layoutCustomisationEnabled, props.schema])

  const [selectedMedia, setSelectedMedia] = useState<IMediaFile | undefined>()

  return (
    <FlowA2 initialValues={initialValues} {...props} schema={schema} onClose={onClose}>
      <StartWithMedia onSelect={setSelectedMedia} />
      {selectedMedia && <SetupBasics selectedMedia={selectedMedia} />}
      <RefineDetails />
      <ChooseTemplate />
      {layoutCustomisationEnabled && <CustomiseLayout />}
    </FlowA2>
  )
})

const FlowA2 = observer(({ children, schema, ...props }: PropsWithChildren<FlowProps<DesignChannel>>) => {
  const layoutCustomisationEnabled = useFlipperFlag('mobile_redesign_wizard_A1_layout_customisation')
  const {
    meta: { channel },
  } = schema

  const { createDesign } = useMobileWizardCreateDesign(channel, {
    navigateToDesignOnComplete: true,
    loadingToast: 'Generating your post...',
  })

  const handleSubmitStep = async ({ currentStep, totalSteps, values, advance, schema }: SubmitStepProps<Values>) => {
    const { stepId } = schema.describe().meta
    if (layoutCustomisationEnabled && stepId === 'ChooseTemplate') {
      const { palette, text_styles: textStyles, source_visual_element_id: templateId } = values

      if (palette && palette.length === 0 && textStyles && textStyles.length === 0) {
        await createDesign(values)
        return
      }

      if (templateId) {
        const res = await VisualElementsClient.getElement(templateId)
        if (res.type !== SUCCESS_RESPONSE_TYPE) {
          reportError(new Error('Failed to fetch template'), { metaData: { templateId } })
          await createDesign(values)
          return
        }
        if (!res.data.basic_layout) {
          await createDesign(values)
          return
        }
      }
    }
    if (currentStep === totalSteps - 1) {
      await createDesign(values)
      return
    }
    advance()
  }

  return (
    <MultiStepMobileWizard<Values> schema={schema} onSubmitStep={handleSubmitStep} {...props}>
      {children}
    </MultiStepMobileWizard>
  )
})

const FlowA2WithTemplateContainer = (
  props: FlowProps<DesignChannel> & { initialValues: Partial<DesignValues> & { source_visual_element_id: number } },
) => {
  const { data: template } = useVisualElement({ id: props.initialValues.source_visual_element_id })

  if (template) {
    if (template.types.includes('video')) {
      return <FlowA2WithInitialTemplateWithVideo {...props} />
    }
    if (template.has_replaceable) {
      return <FlowA2WithInitialTemplateWithReplaceableImage {...props} />
    }

    return <FlowA2WithInitialTemplateWithoutReplaceableImage {...props} />
  }

  return <FlowA2WithoutInitialTemplate {...props} />
}

const FlowA2Container = ({ initialValues, ...props }: FlowProps<DesignChannel>) => {
  const { source_visual_element_id, width, height } = initialValues || {}

  if (source_visual_element_id && width && height) {
    return (
      <FlowA2WithTemplateContainer
        initialValues={{ ...initialValues, source_visual_element_id, width, height }}
        {...props}
      />
    )
  }

  return <FlowA2WithoutInitialTemplate initialValues={initialValues} {...props} />
}

export { FlowA2Container as FlowA2 }
